import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, SkipForward, SkipBack, Volume2, AlertTriangle } from 'lucide-react';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { CustomsGate } from '../../nobel';

interface Trap {
  timestamp: number; // in seconds
  type: 'synonym' | 'number_change' | 'negation';
  description: string;
}

const AudioWaveformTrainer: React.FC = () => {
  const userId = sessionStorage.getItem('ielts_user_id') || '';
  const { canAccess, isLoading, isDev, tier } = useFeatureAccess('advanced_module', userId);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(180); // 3 minutes
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [volume, setVolume] = useState(0.8);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  const traps: Trap[] = [
    { timestamp: 15, type: 'synonym', description: 'Speaker switches "large" to "substantial"' },
    { timestamp: 45, type: 'number_change', description: 'Corrects from "50" to "15"' },
    { timestamp: 90, type: 'negation', description: 'Uses "not uncommon" instead of "common"' },
    { timestamp: 130, type: 'synonym', description: 'Changes "important" to "crucial"' },
  ];

  useEffect(() => {
    if (!isLoading) {
      if (!canAccess && !isDev) {
        setPaywallOpen(true);
      }
    }
  }, [canAccess, isLoading, isDev]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = 3;
      const gap = 2;
      const numBars = Math.floor(canvas.width / (barWidth + gap));

      for (let i = 0; i < numBars; i++) {
        const progress = i / numBars;
        const relativeTime = progress * duration;
        const isCurrent = Math.abs(relativeTime - currentTime) < 1;
        const nearTrap = traps.some(trap =>
          Math.abs(relativeTime - trap.timestamp) < 2
        );

        const baseHeight = Math.random() * 60 + 20;
        const height = isPlaying ? baseHeight * (isCurrent ? 1.5 : 1) : baseHeight * 0.3;

        if (nearTrap) {
          ctx.fillStyle = '#ef4444';
        } else if (isCurrent) {
          ctx.fillStyle = '#C5A059';
        } else if (relativeTime < currentTime) {
          ctx.fillStyle = '#3b82f6';
        } else {
          ctx.fillStyle = '#374151';
        }

        const x = i * (barWidth + gap);
        const y = (canvas.height - height) / 2;
        ctx.fillRect(x, y, barWidth, height);
      }

      traps.forEach(trap => {
        const x = (trap.timestamp / duration) * canvas.width;
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x - 5, 10);
        ctx.lineTo(x + 5, 10);
        ctx.closePath();
        ctx.fill();
        ctx.fillRect(x - 1, 0, 2, canvas.height);
      });

      const playheadX = (currentTime / duration) * canvas.width;
      ctx.strokeStyle = '#C5A059';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(playheadX, 0);
      ctx.lineTo(playheadX, canvas.height);
      ctx.stroke();

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, currentTime, duration]);

  useEffect(() => {
    let interval: number;
    if (isPlaying) {
      interval = window.setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= duration) {
            setIsPlaying(false);
            return duration;
          }
          return prev + (0.1 * playbackSpeed);
        });
      }, 100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, playbackSpeed, duration]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickedTime = (x / canvas.width) * duration;
    setCurrentTime(clickedTime);
  };

  const currentTrap = traps.find(trap =>
    Math.abs(trap.timestamp - currentTime) < 2
  );

  return (
    <>
      <div className="h-full flex flex-col bg-stone-900 rounded-2xl p-8 overflow-hidden">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-black text-white mono uppercase tracking-wider">
            Sonic Trap Detector
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Volume2 size={16} className="text-stone-500" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-20 accent-[#C5A059]"
              />
            </div>
            <select
              value={playbackSpeed}
              onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
              className="bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm mono outline-none"
            >
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1.0x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2.0x</option>
            </select>
          </div>
        </div>

        <div className="bg-black/40 rounded-xl p-6 mb-6 border border-white/10 relative">
          <canvas
            ref={canvasRef}
            width={1000}
            height={150}
            className="w-full cursor-pointer h-32"
            onClick={handleCanvasClick}
          />
          <div className="flex justify-between mt-4 text-xs mono text-stone-500">
            <span>{Math.floor(currentTime / 60)}:{String(Math.floor(currentTime % 60)).padStart(2, '0')}</span>
            <span>{Math.floor(duration / 60)}:{String(duration % 60).padStart(2, '0')}</span>
          </div>
        </div>

        <div className="flex justify-center items-center gap-6 mb-8">
          <button onClick={() => setCurrentTime(Math.max(0, currentTime - 5))} className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
            <SkipBack size={24} className="text-white" />
          </button>
          <button onClick={() => setIsPlaying(!isPlaying)} className="p-6 bg-[#C5A059] rounded-full hover:scale-110 transition-transform shadow-lg shadow-[#C5A059]/20">
            {isPlaying ? <Pause size={32} className="text-stone-900" /> : <Play size={32} className="text-stone-900 ml-1" />}
          </button>
          <button onClick={() => setCurrentTime(Math.min(duration, currentTime + 5))} className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
            <SkipForward size={24} className="text-white" />
          </button>
        </div>

        <div className="h-24">
          {currentTrap && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-red-500/20 border-2 border-red-500 rounded-xl p-4 flex items-center gap-4">
              <div className="p-3 bg-red-500 rounded-full"><AlertTriangle size={24} className="text-white" /></div>
              <div>
                <div className="text-red-400 font-black mono text-sm uppercase mb-1">{currentTrap.type.replace('_', ' ')} DETECTED</div>
                <div className="text-white text-sm">{currentTrap.description}</div>
              </div>
            </motion.div>
          )}
        </div>

        <div className="flex-1 bg-white/5 rounded-xl p-6 overflow-y-auto mt-4 custom-scrollbar">
          <h3 className="text-sm font-black mono text-stone-400 uppercase mb-4">Known Traps</h3>
          <div className="space-y-3">
            {traps.map((trap, idx) => (
              <button key={idx} onClick={() => setCurrentTime(trap.timestamp)} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${currentTrap?.timestamp === trap.timestamp ? 'border-red-500 bg-red-500/10' : 'border-white/10 hover:border-white/30'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs mono text-stone-500">{Math.floor(trap.timestamp / 60)}:{String(Math.floor(trap.timestamp % 60)).padStart(2, '0')}</span>
                  <span className="text-xs mono bg-white/10 px-2 py-1 rounded text-white">{trap.type}</span>
                </div>
                <div className="text-white text-sm">{trap.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <CustomsGate isOpen={paywallOpen} onClose={() => setPaywallOpen(false)} feature="AUDIO WAVEFORM TRAINER" />
    </>
  );
};

export default AudioWaveformTrainer;